/**
 * React hooks for the CRM Object Store.
 *
 * These hooks combine React Query (for fetching, loading/error states) with
 * the centralized store (for real-time state). React Query populates the
 * store on fetch; WebSocket events update it directly. Components read from
 * the store via useSyncExternalStore.
 */

import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSyncExternalStore } from 'react';

import { mergeProjection, useActivePreviewHandle } from '@/lib/crm/preview';
import { logger } from '@/lib/logger';
import type {
  CrmObject,
  CrmObjectFilters,
  CrmObjectsListResponse,
} from '@/types/crm';

import type { EntityKindRef } from '../core/CRMObjectClient';
import { createCRMObjectClient } from '../core/CRMObjectClient';
import type { EntityTypeName } from '../core/types';
import { UUID_RE } from '../core/utils';
import { type CrmObjectListOptions, crmQueryKeys } from '../query/queries';
import {
  clearPendingCreations,
  getObjectByPk,
  getPendingAssociationTargets,
  getPendingCreationsSnapshot,
  getSnapshot,
  getStoreGeneration,
  resolveEntityId,
  type StoredObject,
  subscribe,
  subscribeToObject,
  subscribeToPendingCreations,
  upsertObject,
  upsertSummaryObjects,
} from './crmObjectStore';

// ---------------------------------------------------------------------------
// useStoreSelector — selective subscription for list pages
// ---------------------------------------------------------------------------

/**
 * Shallow compare two string arrays by index.
 */
function arraysShallowEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) {
    return false;
  }
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) {
      return false;
    }
  }
  return true;
}

/**
 * Subscribe to store changes for a specific set of timeline IDs.
 *
 * Returns a Map<timelineId, StoredObject> that only changes identity when
 * one of the watched objects is actually mutated. This prevents the list page
 * from re-rendering when unrelated objects change in the store (e.g., a
 * detail page WS event for an object not on the current list page).
 *
 * Uses useSyncExternalStore with the global subscribe callback, but wraps
 * getSnapshot with a selector that returns a stable reference when none of
 * the watched objects have changed.
 */
function useStoreSelector(
  timelineIds: string[]
): ReadonlyMap<string, StoredObject> {
  const idsRef = useRef<string[]>(timelineIds);
  const resultRef = useRef<ReadonlyMap<string, StoredObject>>(new Map());

  // Only update the ref when the actual IDs change (avoids resetting on every render)
  if (!arraysShallowEqual(idsRef.current, timelineIds)) {
    idsRef.current = timelineIds;
    // Force rebuild with new IDs on next snapshot check
    resultRef.current = new Map();
  }

  const getSelectedSnapshot = useCallback(
    (): ReadonlyMap<string, StoredObject> => {
      const full = getSnapshot();
      const prev = resultRef.current;
      const ids = idsRef.current;

      // Build selected subset and check if anything changed. Each watched id
      // is first resolved through the merge-alias map so pre-merge requests
      // surface the canonical post-merge entry — without this, callers that
      // pass a merged entity's pre-merge id would never see the stored
      // object (keyed by its canonical entity_id).
      let changed = false;
      const selected = new Map<string, StoredObject>();

      for (const id of ids) {
        const canonicalId = resolveEntityId(id);
        const stored = full.get(canonicalId);
        if (stored) {
          // Keyed by the REQUESTED id so callers can look up results with
          // the same id they passed in. Merged entities end up in both
          // slots (requested-id and canonical-id) when both are watched.
          selected.set(id, stored);
          if (!changed && prev.get(id) !== stored) {
            changed = true;
          }
        } else if (!changed && prev.has(id)) {
          changed = true;
        }
      }

      // Size check covers objects removed from the watched set
      if (!changed && selected.size !== prev.size) {
        changed = true;
      }

      if (!changed) {
        return prev;
      } // Stable reference — no re-render

      resultRef.current = selected;
      return selected;
    },
    [] // No deps — reads from refs, which are stable across renders
  );

  return useSyncExternalStore(subscribe, getSelectedSnapshot);
}

// ---------------------------------------------------------------------------
// Association projection drift detection (diagnostic only)
// ---------------------------------------------------------------------------

// Dedupe key -> already warned, so the same mismatch recurring across many
// list pages/refetches doesn't flood the console.
const warnedAssociationsProjectionMismatches = new Set<string>();

/**
 * Compare the server-declared `associations_projection` against the
 * client-derived projection (the `include_associations` keys we actually
 * requested) and warn once per distinct mismatch. This is a deploy-skew /
 * contract-drift detector only — the client-derived set remains what's
 * passed to the store; nothing here changes runtime behavior.
 */
function warnIfAssociationsProjectionMismatch(
  timelineLabel: string,
  declared: string[] | null | undefined,
  derived: string[] | undefined
): void {
  if (declared === undefined || declared === null) {
    // Server didn't declare a projection for this request — nothing to compare.
    return;
  }
  const declaredSorted = [...declared].sort();
  const derivedSorted = [...(derived ?? [])].sort();
  if (arraysShallowEqual(declaredSorted, derivedSorted)) {
    return;
  }
  const key = `${timelineLabel}:${declaredSorted.join(',')}|${derivedSorted.join(',')}`;
  if (warnedAssociationsProjectionMismatches.has(key)) {
    return;
  }
  warnedAssociationsProjectionMismatches.add(key);
  console.warn(
    `[crm] associations_projection mismatch for ${timelineLabel}: server declared [${declaredSorted.join(', ')}], client derived [${derivedSorted.join(', ')}]`
  );
}

/**
 * Derive the client-side association projection from `include_associations`,
 * run the drift diagnostic against the server-declared projection, and
 * return the derived types for use with `upsertSummaryObjects`.
 *
 * An absent or empty `include_associations` must map to `undefined`, not
 * `[]`: `upsertSummaryObjects` treats `[]` as "authoritative for zero
 * types," which would freeze all existing associations instead of leaving
 * them untouched.
 */
function deriveAssociationProjection(
  timelineLabel: string,
  options: CrmObjectListOptions | undefined,
  response: CrmObjectsListResponse
): string[] | undefined {
  const associationProjection = options?.include_associations
    ? Object.keys(options.include_associations)
    : undefined;
  const projectedTypes =
    associationProjection && associationProjection.length > 0
      ? associationProjection
      : undefined;

  warnIfAssociationsProjectionMismatch(
    timelineLabel,
    response.associations_projection,
    associationProjection
  );

  return projectedTypes;
}

// ---------------------------------------------------------------------------
// useStoreObjects — read multiple objects from the store reactively
// ---------------------------------------------------------------------------

/**
 * Subscribe to a set of entity IDs in the store and return their CrmObjects.
 *
 * Only re-renders when the watched objects actually change (uses the same
 * selective subscription as useCrmObjectList). Objects not in the store are
 * silently omitted from the result.
 */
export function useStoreObjects(entityIds: string[]): CrmObject[] {
  const storeMap = useStoreSelector(entityIds);

  return useMemo(() => {
    const result: CrmObject[] = [];
    for (const id of entityIds) {
      const stored = storeMap.get(id);
      if (stored) {
        result.push(stored.data);
      }
    }
    return result;
    // storeMap identity only changes when data changes (useStoreSelector guarantee)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeMap]);
}

/**
 * Subscribe to a set of entity IDs and return a `Map<requestedId, CrmObject>`.
 *
 * Same subscription semantics as `useStoreObjects` but surfaces the requested
 * id → object mapping directly, so callers can look up results with the exact
 * id they passed in. This is the shape `useAssociations` needs — the
 * `target_entity_id` on an association can be a pre-merge id whose canonical
 * entry is keyed by a different `entity_id`. A plain array keyed by
 * `obj.entity_id` would fail that lookup.
 */
export function useStoreObjectsMap(
  entityIds: string[]
): ReadonlyMap<string, CrmObject> {
  const storeMap = useStoreSelector(entityIds);

  return useMemo(() => {
    const result = new Map<string, CrmObject>();
    for (const id of entityIds) {
      const stored = storeMap.get(id);
      if (stored) {
        result.set(id, stored.data);
      }
    }
    return result;
    // storeMap identity only changes when data changes (useStoreSelector guarantee)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeMap]);
}

// ---------------------------------------------------------------------------
// useSelfHealRefetch — shared guard-ref refetch helper
// ---------------------------------------------------------------------------

/**
 * Triggers a guarded refetch when the store entry is missing/incomplete after a
 * successful query. Shared by useCrmObject and SingleSlideout; owns the
 * refetchGuardRef, the guard-key-reset check, the combined condition, the
 * logger.warn call, and the refetch() call.
 *
 * Callers must wrap isObjectMissing in useCallback with the reactive values it
 * reads, so a changed callback identity re-triggers this hook's own effect.
 */
export function useSelfHealRefetch({
  enabled,
  guardKey,
  isObjectMissing,
  querySucceeded,
  queryIsFetching,
  refetch,
  label,
  logContext,
}: {
  enabled: boolean;
  guardKey: string | undefined;
  isObjectMissing: () => boolean;
  querySucceeded: boolean;
  queryIsFetching: boolean;
  refetch: () => void;
  label: string;
  logContext: Record<string, unknown>;
}): void {
  const refetchGuardRef = useRef<string | undefined>(undefined);
  // Read logContext via a ref so changes to context values (e.g. resolved
  // orgId) don't re-trigger the effect — we always log the latest values at
  // the moment of the actual warn/refetch call.
  const logContextRef = useRef(logContext);
  logContextRef.current = logContext;
  useEffect(() => {
    if (refetchGuardRef.current !== guardKey) {
      refetchGuardRef.current = undefined;
    }
    if (
      enabled &&
      isObjectMissing() &&
      querySucceeded &&
      !queryIsFetching &&
      refetchGuardRef.current !== guardKey
    ) {
      refetchGuardRef.current = guardKey;
      logger.warn(label, logContextRef.current);
      void refetch();
    }
  }, [
    enabled,
    isObjectMissing,
    querySucceeded,
    queryIsFetching,
    refetch,
    guardKey,
    label,
  ]);
}

// ---------------------------------------------------------------------------
// useCrmObject — detail / slideout
// ---------------------------------------------------------------------------

export interface UseCrmObjectOptions {
  /** Whether the query is enabled (default: true). */
  enabled?: boolean;
  /**
   * When true, request the response include an `associated_objects` lookup
   * map keyed by entity_id (used to render association badges without N+1
   * follow-up fetches). Defaults to false because computing this map is
   * expensive for high-degree entities (e.g. an `ahoy_org` carrying
   * thousands of visibility associations). Pass `true` only from views
   * that actually consume `associated_objects` — record-detail / full-page
   * record views.
   */
  includeAssociatedObjects?: boolean;
  /**
   * When true, request the response include the access-controlled
   * `associations` array on the object. Defaults to false because the
   * underlying permission check fans out into thousands of DB queries for
   * high-degree entities (15k+ associations on founder/active-user records).
   * Pass `true` only from views that actually read `.associations` —
   * record-detail page, pipeline conflict-retry path. Implied automatically
   * when `includeAssociatedObjects` is true.
   */
  includeAssociations?: boolean;
}

export interface UseCrmObjectResult {
  /** The CRM object (from store if available, else from query). */
  object: CrmObject | undefined;
  /** True during initial fetch (no data yet). */
  isLoading: boolean;
  /** True when a fetch is in flight (including background refetch). */
  isFetching: boolean;
  /** Error from the most recent fetch, if any. */
  error: Error | null;
  /** Manually trigger a refetch. Returns a promise that resolves when complete. */
  refetch: () => Promise<unknown>;
}

/**
 * Hook to get a single CRM object, powered by store + React Query.
 *
 * - React Query handles the fetch, loading states, error handling, refetch-on-focus
 * - On successful fetch, the object is written to the store; the queryFn
 *   itself returns `null` — a status-only sentinel used purely to drive
 *   React Query's loading/success/error lifecycle
 * - Component reads the object exclusively from the store (not from React
 *   Query cache — `query.data` is never the object body)
 * - WebSocket events update the store directly → component re-renders
 * - Per-object subscription: changes to other objects don't trigger re-render
 * - If the store entry is missing after a successful fetch (e.g. an
 *   eviction raced the fetch), the hook self-heals by triggering a refetch
 */
export function useCrmObject(
  orgId: number,
  timelineType: EntityTypeName,
  timelineId: string | undefined,
  options?: UseCrmObjectOptions
): UseCrmObjectResult {
  // Context inside a provider subtree; the ACTIVE registered provider
  // otherwise (out-of-subtree slideout et al.). Null when no provider is
  // mounted anywhere.
  const preview = useActivePreviewHandle();
  // A `create` overlay's synthetic id has no backing record on the server —
  // fetching it would 404. Keyed on `timelineId` (not `resolvedEntityId`,
  // computed later) since a create overlay's id is always a UUID and thus
  // equal to `timelineId` for these callers.
  const hasCreateOverlay =
    preview?.getOverlay(timelineId ?? '')?.kind === 'create';
  const enabled =
    Boolean(orgId && timelineId && (options?.enabled ?? true)) &&
    !hasCreateOverlay;
  const includeAssociatedObjects = options?.includeAssociatedObjects ?? false;
  const includeAssociations = options?.includeAssociations ?? false;

  const isTimelineIdFormat = timelineId ? UUID_RE.test(timelineId) : false;

  // React Query: fetch from server, populate store on success. The queryFn
  // returns `null` — a status-only sentinel. The object body is never read
  // from `query.data`; it's read exclusively from the store below.
  const query = useQuery<null, Error, null>({
    // Include the response-shape flags in the query key so that a lean fetch
    // (e.g. settings page) does not satisfy a request from a caller that
    // needs hydrated `associated_objects` or the `associations` array (e.g.
    // full-page record view), and vice versa. Invalidation keyed by
    // `objectDetail(...)` still matches all shapes via React Query's prefix
    // matching.
    queryKey: timelineId
      ? ([
          ...crmQueryKeys.objectDetail(orgId, timelineType, timelineId),
          includeAssociatedObjects ? 'with-associated' : 'lean',
          includeAssociations ? 'with-associations' : 'no-associations',
        ] as const)
      : ['crm', orgId, 'objects', timelineType, 'detail', '__disabled__'],
    queryFn: async (): Promise<null> => {
      if (!timelineId) {
        throw new Error('timelineId is required');
      }
      const client = createCRMObjectClient(orgId, timelineType);
      const fetchOptions = {
        includeAssociatedObjects,
        includeAssociations,
      };
      // Snapshot the store generation before the fetch: if an org switch (or
      // any other eviction boundary) clears the store while this request is
      // in flight, the generation changes and the write below must be
      // skipped — otherwise a fetch started before the switch could write
      // stale org-A data into the now-cleared org-B store. Mirrors the
      // snapshot/check idiom in entityFetchBatcher.ts's refreshStoreEntities.
      const generationAtStart = getStoreGeneration();
      const obj = isTimelineIdFormat
        ? await client.getByEntityId(timelineId, fetchOptions)
        : await client.get(timelineId, fetchOptions);
      if (getStoreGeneration() !== generationAtStart) {
        logger.debug(
          '[useCrmObject] store generation changed during fetch — discarding stale write',
          { timelineId, timelineType, orgId }
        );
        return null;
      }
      // Seed the hydrated associated objects as SUMMARY entries before the
      // record itself, mirroring the slideout path (useRecordSlideoutQuery).
      // `include_associated_objects=true` already paid to compute and ship
      // this map (hundreds of KB on a high-degree record); without this the
      // full page dropped it on the floor and every consumer re-fetched the
      // same targets over /by-entity-ids/. SUMMARY (isComplete: false) is the
      // honest shape — these carry display properties only, so consumers that
      // need full properties still hydrate, they just paint a name first.
      const associatedObjects = obj.associated_objects;
      if (associatedObjects && Object.keys(associatedObjects).length > 0) {
        upsertSummaryObjects(Object.values(associatedObjects));
      }
      upsertObject(obj); // Write to store
      return null;
    },
    enabled,
    // The store, not this cache, is kept live by WebSocket events — this
    // query is only ever a one-shot fetch that seeds the store. The entry
    // is never rewritten in place, and is only ever removed at explicit
    // eviction boundaries (org switch, list type navigation — see
    // OrgContext.tsx / crm-object-list-page.tsx), so it can never carry a
    // stale body between those boundaries.
    staleTime: Infinity,
  });

  // Read from store — per-object subscription (only re-renders when THIS object changes)
  // Use PK→entity_id lookup if timelineId is a numeric PK, else use timelineId directly.
  // The queryFn upserts+indexes by PK before it resolves; the post-fetch React
  // Query status transition re-renders the hook, at which point getObjectByPk
  // resolves the numeric id. Falls back to the raw timelineId when the PK
  // index doesn't have an entry for it (e.g. the store already keys the
  // object under timelineId directly) — subscribeToObject harmlessly
  // returns undefined if that guess is also wrong.
  const resolvedEntityId = isTimelineIdFormat
    ? timelineId
    : (getObjectByPk(timelineId ?? '')?.entity_id ?? timelineId);

  // Preview overlay for this object, keyed on the SAME id the store
  // subscription below resolves to — a `create` overlay's id is a UUID, so
  // it's found here via `resolvedEntityId === timelineId`.
  const overlaySubscribe = useCallback(
    (cb: () => void) =>
      preview && resolvedEntityId
        ? preview.subscribeToOverlay(resolvedEntityId, cb)
        : () => {},
    [preview, resolvedEntityId]
  );
  const overlayGetSnapshot = useCallback(
    () =>
      preview && resolvedEntityId
        ? preview.getOverlay(resolvedEntityId)
        : undefined,
    [preview, resolvedEntityId]
  );
  const overlay = useSyncExternalStore(overlaySubscribe, overlayGetSnapshot);

  const { subscribe: objSubscribe, getSnapshot: objGetSnapshot } = useMemo(
    () => subscribeToObject(resolvedEntityId ?? ''),
    [resolvedEntityId]
  );
  const storeObject = useSyncExternalStore(objSubscribe, objGetSnapshot);

  // Merge the overlay onto the store object: a `create` overlay produces a
  // synthetic record (no store object yet); an `update` overlay merges
  // overlay-wins properties on top of the store object.
  const mergedObject = useMemo(
    () =>
      overlay
        ? mergeProjection(storeObject, overlay, orgId)
        : (storeObject ?? undefined),
    [overlay, storeObject, orgId]
  );

  // Self-heal when the store entry is missing after a successful fetch (e.g.
  // an eviction boundary — org switch, list type navigation — raced the
  // fetch and cleared the store entry the queryFn just wrote). Guarded on
  // the STABLE timelineId input (not resolvedEntityId, which oscillates
  // null↔uuid for numeric ids as the PK index resolves) so this can't loop.
  const {
    isSuccess: querySucceeded,
    isFetching: queryIsFetching,
    refetch,
  } = query;
  const isObjectMissing = useCallback(() => !storeObject, [storeObject]);
  useSelfHealRefetch({
    enabled,
    guardKey: timelineId,
    isObjectMissing,
    querySucceeded,
    queryIsFetching,
    refetch,
    label: '[useCrmObject] store entry missing after success — refetching',
    logContext: { timelineId, timelineType, orgId },
  });

  return {
    // The object is read exclusively from the store (WS-live), merged with
    // any active preview overlay. The queryFn returns a status-only `null`
    // sentinel, so there is no query.data to fall back to.
    object: mergedObject,
    // An eviction-race refetch (store empty, background fetch in flight,
    // query.isLoading already false) still presents as loading, so callers
    // don't render a false empty/not-found state.
    isLoading: query.isLoading || (!storeObject && query.isFetching),
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
  };
}

// ---------------------------------------------------------------------------
// usePendingCreation404Recovery — record views (slideout, full page)
// ---------------------------------------------------------------------------

/**
 * Discriminates authoritative vs in-flight 404s for record views.
 *
 * An optimistically created entity lives in the store before the server has
 * projected it, so a record view that fetches it by id can receive a 404 for
 * a record the user just created and can see in the list. Treating that 404
 * as authoritative renders "Record not found" for a record that exists
 * client-side — the same misrepresentation the eviction-race guard in
 * SingleSlideout protects against.
 *
 * Returns `true` while `entityId` is a pending optimistic creation, meaning a
 * 404 from the entity fetch must NOT be treated as authoritative: the caller
 * should render store data instead of a not-found state.
 *
 * When the pending state resolves (creation confirmed by a list refetch, or
 * the orphan safety-net timer in `addPendingCreation` expired) while the
 * fetch is sitting on a 404, this hook fires one `refetch`:
 *  - confirmed creation → refetch succeeds → record renders normally;
 *  - orphaned creation → refetch 404s again → with `isPending` now false the
 *    404 is authoritative and the caller's not-found state is correct.
 *
 * 403s are deliberately out of scope — permission denials must keep failing
 * closed regardless of pending state.
 */
export function usePendingCreation404Recovery({
  entityId,
  statusCode,
  refetch,
}: {
  /** Entity id the record view is displaying. */
  entityId: string | undefined;
  /** Status code of the current entity-fetch error, if any. */
  statusCode: number | undefined;
  /** Refetch for the entity query (stable per React Query). */
  refetch: () => Promise<unknown>;
}): boolean {
  const pendingIds = useSyncExternalStore(
    subscribeToPendingCreations,
    getPendingCreationsSnapshot
  );
  const resolvedId = entityId ? resolveEntityId(entityId) : undefined;
  const isPending =
    !!entityId &&
    (pendingIds.has(entityId) || pendingIds.has(resolvedId ?? ''));

  // Sticky latch: once this entityId has been observed pending, it stays
  // "was pending" regardless of render order — unlike a plain previous-value
  // ref, this survives a `statusCode` transition arriving on a render *after*
  // `isPending` has already resolved to false. Both refs reset only when
  // entityId changes (a new pending id starts with no history).
  const entityIdRef = useRef(entityId);
  const everPendingRef = useRef(false);
  const firedRef = useRef(false);
  if (entityIdRef.current !== entityId) {
    entityIdRef.current = entityId;
    everPendingRef.current = false;
    firedRef.current = false;
  }

  // Refetch exactly once per pending id while a 404 is active, so the view
  // converges on the record (confirmed) or an honest not-found (orphaned)
  // instead of staying wedged on the stale error. This single condition
  // covers both render orderings: `isPending` flips to false first and the
  // 404 arrives later, or the 404 is already present when `isPending` flips.
  useEffect(() => {
    if (isPending) {
      everPendingRef.current = true;
    }
    if (
      everPendingRef.current &&
      !isPending &&
      statusCode === 404 &&
      !firedRef.current
    ) {
      firedRef.current = true;
      logger.warn(
        'usePendingCreation404Recovery: recovering stale 404 after pending resolved',
        { entityId, resolvedId }
      );
      void refetch();
    }
  }, [isPending, statusCode, refetch, entityId, resolvedId]);

  return isPending;
}

// ---------------------------------------------------------------------------
// Shared enrichment helpers (used by useCrmObjectList + useCrmObjectListInfinite)
// ---------------------------------------------------------------------------

/**
 * Walk `ids` and return the enriched CrmObject rows plus the set of resolved
 * entity ids that were emitted.
 *
 * Guards applied per row:
 *   (a) Not merged away — after a merge the source id aliases to the survivor's
 *       canonical id; `resolveEntityId` detects this so the merged-away row
 *       doesn't render as a duplicate of the survivor.
 *   (b) Not deleted — avoids briefly showing a deleted record before the
 *       debounced list refetch removes it.
 *   (c) Present in the store — defensive: the store is upserted synchronously
 *       in `queryFn` before ids are returned, so a missing entry should be
 *       rare, but we skip rather than throw.
 *
 * `emittedIds` keys on the RESOLVED entity id (not the raw loop id): two
 * distinct raw ids can resolve onto the same stored object via a merge alias,
 * and keying on the raw id would let both through.
 */
function dedupeEntityRows(
  ids: readonly string[],
  selectedObjects: ReadonlyMap<string, StoredObject>
): { enriched: CrmObject[]; emittedIds: Set<string> } {
  const emittedIds = new Set<string>();
  const enriched: CrmObject[] = [];

  for (const entityId of ids) {
    if (resolveEntityId(entityId) !== entityId) {
      continue;
    }
    const stored = selectedObjects.get(entityId);
    if (!stored || stored.data.deleted_at) {
      continue;
    }
    // Key on the RESOLVED object's id rather than the loop id: two distinct
    // loop ids can resolve onto the same stored object via a merge alias.
    if (emittedIds.has(stored.data.entity_id)) {
      continue;
    }
    emittedIds.add(stored.data.entity_id);
    enriched.push(stored.data);
  }

  return { enriched, emittedIds };
}

/**
 * Walk `pendingIds` and inject any that haven't been emitted yet.
 *
 * `existingIds` is the set of raw ids already returned by the server list
 * response; pending ids present there are skipped (the authoritative row is
 * already in `enriched`). `emittedIds` is mutated in-place so the caller
 * sees the union of both passes — a pending id that resolves to an already-
 * emitted entity (e.g. via a merge alias) is also excluded.
 *
 * Returns the injected rows (to be prepended to `enriched` by the caller).
 */
function injectPendingCreations(
  pendingIds: readonly string[],
  existingIds: ReadonlySet<string>,
  emittedIds: Set<string>,
  selectedObjects: ReadonlyMap<string, StoredObject>
): CrmObject[] {
  const injected: CrmObject[] = [];

  for (const entityId of pendingIds) {
    if (!existingIds.has(entityId)) {
      const stored = selectedObjects.get(entityId);
      // `existingIds` holds RAW ids, so it cannot catch a pending id that
      // resolves onto a row already emitted above via a merge alias.
      // Checking the resolved id via emittedIds closes that gap.
      if (
        stored &&
        !stored.data.deleted_at &&
        !emittedIds.has(stored.data.entity_id)
      ) {
        emittedIds.add(stored.data.entity_id);
        injected.push(stored.data);
      }
    }
  }

  return injected;
}

/**
 * Combine the dedupe + pending-injection passes into a single result, shared
 * by `useCrmObjectList` and `useCrmObjectListInfinite` so the two hooks
 * cannot diverge on how they orchestrate `dedupeEntityRows` and
 * `injectPendingCreations`.
 */
function buildDedupedListResult(
  ids: readonly string[],
  selectedObjects: ReadonlyMap<string, StoredObject>,
  pendingForType: readonly string[]
): { results: CrmObject[]; injectedCount: number } {
  const existingIds = new Set<string>(ids);
  const { enriched, emittedIds } = dedupeEntityRows(ids, selectedObjects);
  const injected = injectPendingCreations(
    pendingForType,
    existingIds,
    emittedIds,
    selectedObjects
  );

  return injected.length > 0
    ? { results: [...injected, ...enriched], injectedCount: injected.length }
    : { results: enriched, injectedCount: 0 };
}

// ---------------------------------------------------------------------------
// useCrmObjectList — list page
// ---------------------------------------------------------------------------

export interface UseCrmObjectListResult {
  /** The enriched list response (objects have latest store data). */
  data: CrmObjectsListResponse | undefined;
  /** True during initial fetch. */
  isLoading: boolean;
  /** True when a fetch is in flight. */
  isFetching: boolean;
  /** Error from the most recent fetch. */
  error: Error | null;
  /**
   * True when `data` is carried over from a previous query key (e.g. a
   * different filter/limit) while the current key's fetch is still in
   * flight or has failed — the shown rows may not reflect the current query.
   */
  isPlaceholderData: boolean;
  /** Manually trigger a refetch. Returns a promise that resolves when complete. */
  refetch: () => Promise<unknown>;
}

/**
 * Cache shape for a single list-query page. Bodies are upserted into the
 * store by the queryFn (`upsertSummaryObjects`); the React Query cache only
 * holds `ids` — the store is the single source of truth for row bodies, so
 * the same object is never duplicated across two caches.
 */
interface CrmObjectListPage {
  ids: string[];
  next: string | null;
  count: number | null;
  associated_objects?: Record<string, CrmObject>;
}

/**
 * Hook for CRM object lists, powered by store + React Query.
 *
 * React Query still owns pagination, total count, and page-membership.
 * The store provides the LATEST property values for objects on the current page.
 * This replaces the overlay store pattern — no manual merge or cleanup needed.
 *
 * How it works:
 * 1. React Query fetches the list from the server
 * 2. On success, all result objects (+ associated objects) are upserted into the store
 * 3. The hook subscribes to the global store snapshot
 * 4. On render, it enriches the query results with store data (if store has newer seq)
 * 5. WS events update the store → useSyncExternalStore triggers re-render →
 *    enrichment produces new results → RAC Table re-renders correctly
 */
export function useCrmObjectList(
  orgId: number,
  timelineType: EntityTypeName,
  filters?: CrmObjectFilters,
  options?: CrmObjectListOptions
): UseCrmObjectListResult {
  const client = useMemo(
    () => createCRMObjectClient(orgId, timelineType),
    [orgId, timelineType]
  );

  // React Query: fetch list, populate store with results + associated objects
  const query = useQuery({
    queryKey: crmQueryKeys.objectsListUnified(
      orgId,
      timelineType,
      'paged',
      'ids',
      {
        filters,
        include_associated_objects:
          options?.include_associated_objects ?? false,
        include_associations: options?.include_associations ?? null,
      }
    ),
    queryFn: async (): Promise<CrmObjectListPage> => {
      const response = await client.list(filters, options);

      const projectedTypes = deriveAssociationProjection(
        timelineType,
        options,
        response
      );

      // Write list results as *summary* entries (isComplete: false).
      // The query API may return only requested properties (not the full
      // object shape). Storing as complete would overwrite richer entries
      // from prior detail fetches, erasing properties not in the list
      // response. Storing as incomplete means:
      //   - Existing complete entries are preserved (never downgraded)
      //   - The enrichment logic below reads row bodies straight from the
      //     store, so the list page displays whatever the query result
      //     stored — which already has all properties the list page needs
      //   - Detail fetches and WS event accumulation still produce complete
      //     entries that the enrichment logic can use
      upsertSummaryObjects(response.results, projectedTypes);

      // Upsert associated objects as *summary* (isComplete: false).
      // These contain only display properties (display_name, domain, email, etc.)
      // from the list API. Storing as incomplete means:
      //   - subscribeToObject() returns undefined → slideouts do a full fetch
      //   - Enrichment guards skip incomplete entries
      //   - If the store already has a complete entry, it's preserved
      if (response.associated_objects) {
        upsertSummaryObjects(Object.values(response.associated_objects));
      }

      // Cache only ids — bodies live in the store (upserted above), never
      // duplicated into the React Query cache. Server order is authoritative.
      return {
        ids: response.results.map(r => r.entity_id),
        next: response.next,
        count: response.count,
        associated_objects: response.associated_objects,
      };
    },
    enabled: (options?.enabled ?? true) && !!orgId && orgId > 0,
    placeholderData: keepPreviousData,
  });

  // Subscribe to pending creations for optimistic insertion of new objects.
  // When a WS `ahoy_object_created` event arrives, the object is inserted
  // into the store and tracked here. This lets us inject it into the list
  // results immediately, before the debounced refetch completes.
  const pendingIds = useSyncExternalStore(
    subscribeToPendingCreations,
    getPendingCreationsSnapshot
  );

  // Filter pending IDs to this entity type, and optionally by association.
  // When the list filters by `associated_with_entity_id`, only inject pending
  // creations that were associated with that entity (e.g., notes on a contact).
  const associatedWithEntityId = filters?.associated_with_entity_id;
  // Same rule as the association guard below, extended to search: only inject
  // pending creations the active query would actually return. An active search
  // is resolved server-side and cannot be reproduced here, so injecting an
  // unmatched pending creation renders a phantom row and inflates the count.
  // Keyed on `search` only, not `filter_groups` (see useCrmObjectListInfinite).
  const hasActiveSearch =
    typeof filters?.search === 'string' && filters.search.trim() !== '';
  const pendingForType = useMemo(() => {
    if (hasActiveSearch) {
      return [];
    }
    const ids: string[] = [];
    const storeSnapshot = getSnapshot();
    for (const entityId of pendingIds) {
      const stored = storeSnapshot.get(entityId);
      if (stored?.data.entity_type_name !== timelineType) {
        continue;
      }
      // If filtering by association, only include pending objects
      // that have the matching association target
      if (associatedWithEntityId) {
        const targets = getPendingAssociationTargets(entityId);
        if (!targets?.has(associatedWithEntityId)) {
          continue;
        }
      }
      ids.push(entityId);
    }
    return ids;
  }, [pendingIds, timelineType, associatedWithEntityId, hasActiveSearch]);

  // Extract timeline IDs of all objects we need to watch in the store.
  // This powers the selective subscription below — only re-render when
  // objects visible on this page change, not on any store mutation.
  // Includes pending creation IDs so we subscribe to their store entries.
  const watchedIds = useMemo(() => {
    const ids: string[] = [];
    if (query.data?.ids) {
      ids.push(...query.data.ids);
    }
    if (query.data?.associated_objects) {
      for (const obj of Object.values(query.data.associated_objects)) {
        ids.push(obj.entity_id);
      }
    }
    // Include pending creation IDs so useStoreSelector picks them up
    for (const id of pendingForType) {
      if (!ids.includes(id)) {
        ids.push(id);
      }
    }
    return ids;
  }, [query.data?.ids, query.data?.associated_objects, pendingForType]);

  // Subscribe to only the objects visible on this page.
  // Returns a stable Map reference when none of the watched objects changed.
  const selectedObjects = useStoreSelector(watchedIds);

  // Resolve row bodies from the store by id.
  // Guards:
  //   (a) not merged away — drop rows whose id now aliases to a different
  //       canonical entity (the survivor renders in its own slot)
  //   (b) not deleted — avoid briefly showing a deleted record in the list
  //       before the debounced list invalidation removes it via refetch
  //   (c) present in the store — defensive: the store is upserted
  //       synchronously in queryFn before these ids are returned, so an id
  //       with no store entry should not occur in practice. Skip it rather
  //       than throw or render a phantom empty row; the row appears once
  //       the store catches up.
  //
  // Also injects pending creations (objects created via WS events that
  // haven't appeared in a list refetch yet) at the top of results.
  const { results: enrichedResults, injectedCount } = useMemo(() => {
    if (!query.data?.ids) {
      return { results: [] as CrmObject[], injectedCount: 0 };
    }

    return buildDedupedListResult(
      query.data.ids,
      selectedObjects,
      pendingForType
    );
  }, [query.data?.ids, selectedObjects, pendingForType]);

  // Enrich associated objects with latest store data (same guards).
  const enrichedAssociatedObjects = useMemo(() => {
    if (!query.data?.associated_objects) {
      return undefined;
    }
    const result: Record<string, CrmObject> = {};
    for (const [key, obj] of Object.entries(query.data.associated_objects)) {
      const stored = selectedObjects.get(obj.entity_id);
      result[key] =
        stored &&
        stored.meta.lastSeq > obj.last_event_seq &&
        stored.meta.fetchedAt > 0 &&
        !stored.data.deleted_at
          ? stored.data
          : obj;
    }
    return result;
  }, [query.data?.associated_objects, selectedObjects]);

  const enrichedData = useMemo((): CrmObjectsListResponse | undefined => {
    if (!query.data) {
      return undefined;
    }
    // Adjust the count for optimistically injected pending creations only.
    // Deduped/dropped server rows (merge aliases, deletes, duplicate ids) must
    // NOT lower the count: `enrichedResults.length - ids.length` would go
    // negative on a deduped row and undercount the list, skewing pagination
    // and select-all totals. `injectedCount` comes from the memo and counts
    // exactly the injected pending creations.
    return {
      next: query.data.next,
      previous: null,
      results: enrichedResults,
      count:
        query.data.count === null ? null : query.data.count + injectedCount,
      ...(enrichedAssociatedObjects !== undefined && {
        associated_objects: enrichedAssociatedObjects,
      }),
    };
  }, [query.data, enrichedResults, injectedCount, enrichedAssociatedObjects]);

  // Clear pending creations AFTER the query data has been committed to React
  // state. Clearing inside queryFn causes a flash: the pending object
  // disappears (clear) before the server response renders (query update).
  // By clearing in useEffect, the server results are already rendered, so
  // removing the pending entry doesn't create a visible gap.
  //
  // We clear only the specific IDs that appear in the server response
  // (not all pending IDs of this type) to avoid dropping newer pending
  // creations that arrived after this fetch started.
  useEffect(() => {
    if (pendingForType.length > 0 && query.data?.ids) {
      const resultIds = new Set(query.data.ids);
      const confirmedIds = pendingForType.filter(id => resultIds.has(id));
      if (confirmedIds.length > 0) {
        clearPendingCreations(confirmedIds);
      }
    }
  }, [query.data?.ids, pendingForType]);

  return {
    data: enrichedData,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    isPlaceholderData: query.isPlaceholderData,
    refetch: query.refetch,
  };
}

// ---------------------------------------------------------------------------
// useCrmObjectListInfinite — infinite scroll list page
// ---------------------------------------------------------------------------

// Short window so re-visits within it don't refetch cold; WebSocket keeps the data live regardless.
const CRM_LIST_STALE_TIME_MS = 30_000;

/**
 * Query options factory for the CRM object list infinite query.
 *
 * Produces an identical query key/body to useCrmObjectListInfinite so callers
 * (e.g. usePrefetchCRMList) can prime the same cache entry.
 */
export function crmObjectListInfiniteQueryOptions(
  orgId: number,
  timelineType: EntityTypeName | EntityKindRef,
  filters?: Omit<CrmObjectFilters, 'page'>,
  options?: CrmObjectListOptions
) {
  const pageSize = (filters?.page_size as number | undefined) ?? 50;
  const client = createCRMObjectClient(orgId, timelineType);
  const timelineLabel =
    typeof timelineType === 'string'
      ? timelineType
      : `kind:${timelineType.entity_kind}`;

  const queryKey =
    typeof timelineType === 'string'
      ? crmQueryKeys.objectsListUnified(
          orgId,
          timelineType,
          'infinite',
          'ids',
          {
            filters,
            page_size: pageSize,
            include_associated_objects:
              options?.include_associated_objects ?? false,
            include_associations: options?.include_associations ?? null,
          }
        )
      : [
          ...crmQueryKeys.objectsListByKind(
            orgId,
            timelineType.entity_kind,
            filters
          ),
          'infinite',
          'ids',
          pageSize,
          options?.include_associated_objects ?? false,
          JSON.stringify(options?.include_associations ?? null),
        ];

  return {
    queryKey,
    placeholderData: keepPreviousData,
    queryFn: async ({
      pageParam,
    }: {
      pageParam: number;
    }): Promise<CrmObjectListPage> => {
      const response = await client.list(
        { ...filters, page: pageParam, page_size: pageSize },
        options
      );

      const projectedTypes = deriveAssociationProjection(
        timelineLabel,
        options,
        response
      );

      // Write list results as *summary* entries (isComplete: false)
      upsertSummaryObjects(response.results, projectedTypes);

      // Upsert associated objects as *summary* (isComplete: false)
      if (response.associated_objects) {
        upsertSummaryObjects(Object.values(response.associated_objects));
      }

      // Cache only ids — bodies live in the store (upserted above), never
      // duplicated into the React Query cache. Server order is authoritative.
      return {
        ids: response.results.map(r => r.entity_id),
        next: response.next,
        count: response.count,
        associated_objects: response.associated_objects,
      };
    },
    initialPageParam: 1,
    getNextPageParam: (
      lastPage: CrmObjectListPage,
      allPages: CrmObjectListPage[]
    ) => {
      // A short last page means the server has no further rows at this offset,
      // regardless of `count` — stop. This also bounds the count-based branch
      // below: once the offset runs past the result set the page comes back
      // short and pagination terminates even if overlap kept unique coverage
      // below `count`.
      if (lastPage.ids.length < pageSize) {
        return undefined;
      }
      if (lastPage.count === null) {
        return allPages.length + 1;
      }
      // Count overlapping rows once. A record that shifts across a page
      // boundary between fetches (a row inserted above it, a re-sort) is
      // genuinely returned on two pages; summing raw page lengths would
      // overcount and declare pagination complete before every unique row has
      // loaded, silently truncating the list. Compare UNIQUE loaded ids
      // against the server total so fetching continues until coverage is met.
      const uniqueFetched = new Set<string>();
      for (const page of allPages) {
        for (const id of page.ids) {
          uniqueFetched.add(id);
        }
      }
      return uniqueFetched.size < lastPage.count
        ? allPages.length + 1
        : undefined;
    },
    enabled: (options?.enabled ?? true) && !!orgId && orgId > 0,
    staleTime: CRM_LIST_STALE_TIME_MS,
  };
}

/** Response shape for infinite scroll list hook (no next/previous pagination links). */
interface CrmObjectListInfiniteData {
  results: CrmObject[];
  count: number | null;
  associated_objects?: Record<string, CrmObject>;
}

export interface UseCrmObjectListInfiniteResult {
  /** The enriched list response (flattened from all pages). */
  data: CrmObjectListInfiniteData | undefined;
  /** True during initial fetch (no data yet). */
  isLoading: boolean;
  /** True when a fetch is in flight (including background refetch). */
  isFetching: boolean;
  /** True when showing stale data from a previous query key while refetching. */
  isPlaceholderData: boolean;
  /** True when fetching the next page. */
  isFetchingNextPage: boolean;
  /** True if there are more pages to fetch. */
  hasNextPage: boolean;
  /** Fetch the next page. */
  fetchNextPage: () => void;
  /** Error from the most recent fetch. */
  error: Error | null;
  /** Manually trigger a refetch. Returns a promise that resolves when complete. */
  refetch: () => Promise<unknown>;
  /**
   * Total row count adjusted for optimistically injected pending creations
   * (kept consistent with data.count), so it is never lower than the rows in
   * hand. May legitimately exceed the loaded count during infinite scroll.
   * Null when the server count was not requested.
   */
  totalCount: number | null;
}

/**
 * Hook for CRM object lists with infinite scroll, powered by store + React Query.
 *
 * Similar to useCrmObjectList but uses useInfiniteQuery for pagination.
 * Pages are accumulated and flattened into a single results array.
 *
 * How it works:
 * 1. React Query fetches pages of results using offset-based pagination
 * 2. On success, all result objects (+ associated objects) are upserted into the store
 * 3. The hook subscribes to the global store snapshot
 * 4. On render, it enriches the query results with store data (if store has newer seq)
 * 5. WS events update the store → useSyncExternalStore triggers re-render
 *
 * @param orgId - Organization ID
 * @param timelineType - Entity type (e.g., 'ahoy_contact')
 * @param filters - Filters to apply (page is managed internally, page_size defaults to 50)
 * @param options - Additional query options
 */
export function useCrmObjectListInfinite(
  orgId: number,
  timelineType: EntityTypeName | EntityKindRef,
  filters?: Omit<CrmObjectFilters, 'page'>,
  options?: CrmObjectListOptions
): UseCrmObjectListInfiniteResult {
  // prettier-ignore
  const query = useInfiniteQuery(crmObjectListInfiniteQueryOptions(orgId, timelineType, filters, options));

  // Subscribe to pending creations for optimistic insertion of new objects
  const pendingIds = useSyncExternalStore(
    subscribeToPendingCreations,
    getPendingCreationsSnapshot
  );

  // Filter pending IDs to this entity type
  const rawAssociatedWithEntityId = filters?.associated_with_entity_id;
  // Type guard: associated_with_entity_id is always string if defined
  const associatedWithEntityId =
    typeof rawAssociatedWithEntityId === 'string'
      ? rawAssociatedWithEntityId
      : undefined;
  // An active search is resolved server-side (case-insensitive label search
  // across `search_properties`) and cannot be faithfully reproduced here.
  // Injecting a pending creation the search would not have returned renders a
  // phantom row and inflates totalCount, which suppresses the "no results"
  // empty state until the 60s orphan timer in `addPendingCreation` fires.
  // Defer to the server while searching: the creation stays pending and is
  // confirmed by the next refetch that does return it.
  //
  // Deliberately keyed on `search` only, NOT on `filter_groups` — lists such
  // as Deals always carry `defaultFilterConditions`, so gating on filters
  // would disable optimistic insertion on those lists entirely.
  const hasActiveSearch =
    typeof filters?.search === 'string' && filters.search.trim() !== '';
  const pendingForType = useMemo(() => {
    // Kind-based lists can aggregate multiple entity types — there is no single
    // entity_type_name to match pending creations against, so optimistic
    // injection is disabled for kind refs. Objects created via the builder flow
    // navigate away and remount, triggering a fresh refetch instead.
    if (typeof timelineType !== 'string' || hasActiveSearch) {
      return [];
    }
    const ids: string[] = [];
    const storeSnapshot = getSnapshot();
    for (const entityId of pendingIds) {
      const stored = storeSnapshot.get(entityId);
      if (stored?.data.entity_type_name !== timelineType) {
        continue;
      }
      if (associatedWithEntityId) {
        const targets = getPendingAssociationTargets(entityId);
        if (!targets?.has(associatedWithEntityId)) {
          continue;
        }
      }
      ids.push(entityId);
    }
    return ids;
  }, [pendingIds, timelineType, associatedWithEntityId, hasActiveSearch]);

  // Flatten all pages into a single ids array
  const flatIds = useMemo(() => {
    if (!query.data?.pages) {
      return [];
    }
    return query.data.pages.flatMap(page => page.ids);
  }, [query.data?.pages]);

  // Merge associated objects from all pages
  const mergedAssociatedObjects = useMemo(() => {
    if (!query.data?.pages) {
      return undefined;
    }
    const merged: Record<string, CrmObject> = {};
    for (const page of query.data.pages) {
      if (page.associated_objects) {
        Object.assign(merged, page.associated_objects);
      }
    }
    return Object.keys(merged).length > 0 ? merged : undefined;
  }, [query.data?.pages]);

  // Extract timeline IDs of all objects we need to watch
  const watchedIds = useMemo(() => {
    const ids: string[] = [...flatIds];
    if (mergedAssociatedObjects) {
      for (const obj of Object.values(mergedAssociatedObjects)) {
        ids.push(obj.entity_id);
      }
    }
    for (const id of pendingForType) {
      if (!ids.includes(id)) {
        ids.push(id);
      }
    }
    return ids;
  }, [flatIds, mergedAssociatedObjects, pendingForType]);

  // Subscribe to only the objects visible across all pages
  const selectedObjects = useStoreSelector(watchedIds);

  // Resolve row bodies from the store by id (same guards as the paged hook —
  // not merged away, not deleted, present in the store).
  const { results: enrichedResults, injectedCount } = useMemo(() => {
    // `flatIds` is every page concatenated, so a record that shifts across a
    // page boundary between fetches (a new row inserted above it, a re-sort)
    // is genuinely returned on two pages and would render twice — dedup is
    // especially important here.
    return buildDedupedListResult(flatIds, selectedObjects, pendingForType);
  }, [flatIds, selectedObjects, pendingForType]);

  // Enrich associated objects with latest store data
  const enrichedAssociatedObjects = useMemo(() => {
    if (!mergedAssociatedObjects) {
      return undefined;
    }
    const result: Record<string, CrmObject> = {};
    for (const [key, obj] of Object.entries(mergedAssociatedObjects)) {
      const stored = selectedObjects.get(obj.entity_id);
      result[key] =
        stored &&
        stored.meta.lastSeq > obj.last_event_seq &&
        stored.meta.fetchedAt > 0 &&
        !stored.data.deleted_at
          ? stored.data
          : obj;
    }
    return result;
  }, [mergedAssociatedObjects, selectedObjects]);

  // Raw server total from the first page (all pages share the same count).
  // Preserve null when count was not requested.
  const rawTotalCount = query.data?.pages[0]?.count ?? null;

  // Injected-adjusted total. `injectedCount` (from the enrichedResults memo)
  // counts ONLY the injected pending creations, never deduped/dropped server
  // rows: a page-boundary duplicate removed by the dedupe guard must not lower
  // the count. `enrichedResults.length - flatIds.length` would go negative on
  // such a duplicate and undercount the list — and, since the table
  // virtualizer trusts this over the rows array, could even drop below the
  // rows actually in hand.
  const totalCount =
    rawTotalCount != null ? rawTotalCount + injectedCount : null;

  const enrichedData = useMemo((): CrmObjectListInfiniteData | undefined => {
    if (!query.data?.pages || query.data.pages.length === 0) {
      return undefined;
    }
    return {
      results: enrichedResults,
      count: totalCount,
      ...(enrichedAssociatedObjects !== undefined && {
        associated_objects: enrichedAssociatedObjects,
      }),
    };
  }, [
    query.data?.pages,
    enrichedResults,
    totalCount,
    enrichedAssociatedObjects,
  ]);

  // Clear pending creations after query data is committed
  useEffect(() => {
    if (pendingForType.length > 0 && flatIds.length > 0) {
      const resultIds = new Set(flatIds);
      const confirmedIds = pendingForType.filter(id => resultIds.has(id));
      if (confirmedIds.length > 0) {
        clearPendingCreations(confirmedIds);
      }
    }
  }, [flatIds, pendingForType]);

  return {
    data: enrichedData,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isPlaceholderData: query.isPlaceholderData,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage ?? false,
    fetchNextPage: query.fetchNextPage,
    error: query.error,
    refetch: query.refetch,
    totalCount,
  };
}
